import React, { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import SearchBar from "../Common/SearchBar";
import DocumentRecordCard from "./DocumentRecordCard";
import { getDocumentNumber, getDocumentStatus, getPartyName, getRecordKey } from "./documentHelpers";

const normalizeStatusKey = (value) => String(value || "all").toLowerCase().replace(/\s+/g, "_");

const DocumentListPanel = ({ rows = [], selectedRow, config, onSelect, onBack }) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const statusOptions = config.statusOptions || ["All", "Draft", "Sent", "Paid", "Cancelled"];
  const selectedKey = getRecordKey(selectedRow || {}, config, 0);

  const statusCounts = useMemo(() => {
    const counts = { all: rows.length };
    statusOptions.forEach((option) => {
      const key = normalizeStatusKey(option);
      if (key === "all") return;
      counts[key] = rows.filter((row) => normalizeStatusKey(getDocumentStatus(row, config).label) === key).length;
    });
    return counts;
  }, [config, rows, statusOptions]);

  const filteredRows = useMemo(() => {
    const search = query.trim().toLowerCase();
    return rows.filter((row) => {
      const status = normalizeStatusKey(getDocumentStatus(row, config).label);
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesSearch =
        !search ||
        [getDocumentNumber(row, config), getPartyName(row, config), row.status, row.reference, row.total, row.amount]
          .some((value) => String(value || "").toLowerCase().includes(search));
      return matchesStatus && matchesSearch;
    });
  }, [config, query, rows, statusFilter]);

  return (
    <aside className="document-list-panel">
      <div className="document-list-head">
        <button className="document-back-btn" onClick={onBack} aria-label={`Back to ${config.title} list`}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1>{config.title}</h1>
          <p>
            {rows.length} {config.countLabel || config.title.toLowerCase()}
          </p>
        </div>
      </div>

      <div className="document-list-tools">
        <SearchBar
          value={query}
          onChange={setQuery}
          placeholder={`Search ${config.countLabel || config.title.toLowerCase()}...`}
          className="document-list-search"
        />
      </div>

      <div className="document-status-tabs">
        {statusOptions.map((option) => {
          const key = normalizeStatusKey(option);
          return (
            <button key={option} className={statusFilter === key ? "is-active" : ""} onClick={() => setStatusFilter(key)}>
              {option} <span>{statusCounts[key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="document-list-scroll">
        {filteredRows.length === 0 && <div className="document-empty">No records found.</div>}
        {filteredRows.map((row, index) => (
          <DocumentRecordCard
            key={getRecordKey(row, config, index)}
            row={row}
            config={config}
            active={getRecordKey(row, config, index) === selectedKey}
            onClick={() => onSelect(row)}
          />
        ))}
      </div>
    </aside>
  );
};

export default DocumentListPanel;
